import { DataGrid, GridToolbarContainer, GridActionsCellItem } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { Button, Dialog, TextField, DialogTitle, Stack, DialogActions, DialogContent, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useContext } from 'react'
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { ApiContext } from "../../../App";

const PRICE_ENGINES = {
    fixed: 'fixed',
    day_night: 'day_night',
    octopus: 'octopus',
    epias: 'epias',
    epias_gas: 'epias_gas'
}

const DEFAULT_TARIFF_DATA = {
    name: "",
    price_engine: "",
    value: "",
    night_value: "",
    day_tariff_start_time: "",
    day_tariff_end_time: "",
    carbon_intensity_g_kWh: "177"
}


const TariffDialog = ({isOpen, onClose, onChange, onSave, dialogData, errorMessages}) => {

    const { t } = useTranslation();

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth={"md"}>
                    <DialogTitle>{t("Edit tariff")}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <TextField
                                variant="standard"
                                id="name"
                                label={t("Name")}
                                value={dialogData.name}
                                onChange={(event) => onChange("name", event.target.value)}
                                error={errorMessages.hasOwnProperty("name")}
                                helperText={errorMessages["name"]? errorMessages["name"]: ""}
                            />
                            <TextField
                                variant="standard"
                                id="price_engine"
                                label={t("Price Engine")}
                                value={dialogData.price_engine}
                                onChange={(event) => onChange("price_engine", event.target.value)}
                                error={errorMessages.hasOwnProperty("price_engine")}
                                helperText={errorMessages["price_engine"]? errorMessages["price_engine"]: ""}
                                select
                            >
                                {Object.values(PRICE_ENGINES).map((price_engine, index) => (
                                    <MenuItem value={price_engine} key={index}>
                                        {price_engine}
                                    </MenuItem>
                                ))}
                            </TextField>
                            { [PRICE_ENGINES.fixed, PRICE_ENGINES.day_night].includes(dialogData.price_engine) && <TextField
                                variant="standard"
                                id="value"
                                label={t("Value")}
                                value={dialogData.value ?? ""}
                                onChange={(event) => onChange("value", event.target.value)}
                                error={errorMessages.hasOwnProperty("value")}
                                helperText={errorMessages["value"]? errorMessages["value"]: ""}
                            />}
                            {dialogData.price_engine === PRICE_ENGINES.day_night &&
                                <>
                                    <TextField
                                        variant="standard"
                                        id="night_value"
                                        label={t("Night value")}
                                        value={dialogData.night_value ?? ""}
                                        onChange={(event) => onChange("night_value", event.target.value)}
                                        error={errorMessages.hasOwnProperty("night_value")}
                                        helperText={errorMessages["night_value"]? errorMessages["night_value"]: ""}
                                    />
                                    <TextField
                                        variant="standard"
                                        id="day_tariff_start_time"
                                        label={t("Day tariff start time")}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder={"HH:MM"}
                                        value={dialogData.day_tariff_start_time ?? ""}
                                        onChange={(event) => onChange("day_tariff_start_time", event.target.value)}
                                        error={errorMessages.hasOwnProperty("day_tariff_start_time")}
                                        helperText={errorMessages["day_tariff_start_time"]? errorMessages["day_tariff_start_time"]: ""}
                                    />
                                    <TextField
                                        variant="standard"
                                        id="day_tariff_end_time"
                                        label={t("Day tariff end time")}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder={"HH:MM"}
                                        value={dialogData.day_tariff_end_time ?? ""}
                                        onChange={(event) => onChange("day_tariff_end_time", event.target.value)}
                                        error={errorMessages.hasOwnProperty("day_tariff_end_time")}
                                        helperText={errorMessages["day_tariff_end_time"]? errorMessages["day_tariff_end_time"]: ""}
                                    />
                                </>
                            }
                            <TextField
                                variant="standard"
                                id="carbon_intensity_g_kWh"
                                label={t("Carbon intensity (g/kWh)")}
                                value={dialogData.carbon_intensity_g_kWh}
                                onChange={(event) => onChange("carbon_intensity_g_kWh", event.target.value)}
                                error={errorMessages.hasOwnProperty("carbon_intensity_g_kWh")}
                                helperText={errorMessages["carbon_intensity_g_kWh"]? errorMessages["carbon_intensity_g_kWh"]: ""}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} type="button">{t("Cancel")}</Button>
                        <Button type="button" onClick={onSave}>{t("Save")}</Button>
                    </DialogActions>
            </Dialog>
    )
}


const TariffTable = ({ facility }) => {
    const { t } = useTranslation();
    const [tariffRows, setTariffRows] = useState([])
    const [tariffDialogIsOpen, setTariffDialogIsOpen] = useState(false)
    const [errorMessages, setErrorMessages] = useState({})
    const [dialogData, setDialogData] = useState(DEFAULT_TARIFF_DATA)

    const {tariffApi} = useContext(ApiContext)

    useEffect(() => {
        tariffApi.list(facility, (data) => {
            setTariffRows(data)
        }, () => {
            alert(t("Couldn't get tariff data"))
        })
    }, [facility])

    const handleDeleteTariff = (id) => {
        const confirmed = window.confirm(t('Delete this tariff?'))
        if (confirmed) {
            tariffApi.delete(id, () => {
                setTariffRows(tariffRows.filter((row) => row.url !== id))
            }, () => {
                alert(t('Could not delete tariff'))
            })
        }
    }

    const handleEditTariff = (id) => {
        setTariffDialogIsOpen(true)
        const selectedTariffData = tariffRows.find((row) => row.url === id)
        setDialogData(selectedTariffData)
        setErrorMessages({})
    }

    const handleAddTariff = () => {
        setTariffDialogIsOpen(true)
        setDialogData({...DEFAULT_TARIFF_DATA, facility: facility})
        setErrorMessages({})
    }

    const handleCancel = () => {
        setTariffDialogIsOpen(false)
    }

    const handleChange = (field, value) => {
        setDialogData({ ...dialogData, [field]: value })
    }

    const handleSave = () => {

        console.log(dialogData)

        const apiData = Object.fromEntries(Object.entries(dialogData).filter(([, value]) => value !== ""))

        if (dialogData.url) {
            tariffApi.update(
                dialogData.url,
                apiData,
                () => {setTariffRows(
                    tariffRows.map((row)=> dialogData.url === row.url ? dialogData : row)
                );
                    setTariffDialogIsOpen(false);
                },
                (errors) => setErrorMessages(errors)
            )
        } else {
            tariffApi.create(
                apiData,
                (data) => {
                    setTariffRows([...tariffRows, { ...dialogData, url: data.url }])
                    setTariffDialogIsOpen(false)
                },
                (errors) => setErrorMessages(errors)
            )
        }
    }

    const columns = [
        {
            field: "name",
            headerName: t("Name"),
            width: 175
        },
        {
            field: "price_engine",
            headerName: t("Price Engine"),
            width: 175
        },
        {
            field: "value",
            headerName: t("Value"),
            width: 175
        },
        {
            field: "night_value",
            headerName: t("Night Value"),
            width: 175
        },
        {
            field: "day_tariff_start_time",
            headerName: t("Day tariff start time"),
            width: 175
        },
        {
            field: "day_tariff_end_time",
            headerName: t("Day tariff end time"),
            width: 175
        },
        {
            field: "carbon_intensity_g_kWh",
            headerName: t("Carbon intensity (g/kWh)"),
            width: 175
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Actions'),
            flex: 0.5,
            minWidth: 150,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => handleEditTariff(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => handleDeleteTariff(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ]


    const TariffToolbar = () => {
        return (
            <GridToolbarContainer>
                <Button color="primary" size="small" startIcon={<AddIcon />} onClick={handleAddTariff}>
                    {t("Add Tariff")}
                </Button>
            </GridToolbarContainer>
        )
    }

    return (
        <Box sx={{ height: 400, width: '100%', marginTop: '20px' }}>
            <DataGrid
                columns={columns}
                rows={tariffRows}
                getRowId={(row) => row.url}
                components={{
                    Toolbar: TariffToolbar
                }}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                }}
                pageSizeOptions={[5]}
            />
            <TariffDialog
                isOpen={tariffDialogIsOpen}
                onClose={handleCancel}
                onSave={handleSave}
                onChange={handleChange}
                dialogData={dialogData}
                errorMessages={errorMessages}
            />
        </Box>
    );
}

export default TariffTable;